import i18n from "i18next";
import { Alert, Box } from "@mui/material";
import { faTrash } from "@fortawesome/pro-regular-svg-icons";
import { VerticalBox } from "src/components/common/box/VerticalBox.tsx";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import { PropertyModelInputField } from "src/components/views/erp/configurator/configuratorForm/propertyModel/PropertyModelInputField.tsx";
import {
	PartConfigurationSchemaProperty,
	PartConfigurationSchemaValidationIssue,
	PartConfigurationValue,
} from "src/components/views/erp/configurator/configuratorForm/propertyModel/partConfigurationSdk.ts";
import {
	getUnevaluatedChildIndexes,
	isSubConfigurationProperty,
	PropertyInputValues,
	PropertyModelNode,
} from "src/components/views/erp/configurator/configuratorForm/propertyModel/propertyModelInputTree.ts";

export interface PropertyModelConfigurationNodeFormProps {
	node: PropertyModelNode;
	/** The properties of the node's model, in layout order. */
	schemaProperties: PartConfigurationSchemaProperty[];
	issues: PartConfigurationSchemaValidationIssue[];
	inputValues: PropertyInputValues;
	readonly: boolean;
	onInputChanged: (propertyName: string, value: PartConfigurationValue | undefined) => Promise<unknown>;
	/** Removes the given children of [propertyName] at [indexes]. */
	removeChildren: (propertyName: string, indexes: number[]) => Promise<unknown>;
}

/** The inputs of one configuration of a property-model tree, in the order of its properties. */
export function PropertyModelConfigurationNodeForm({
	node,
	schemaProperties,
	issues,
	inputValues,
	readonly,
	onInputChanged,
	removeChildren,
}: PropertyModelConfigurationNodeFormProps) {
	const evaluationsByName = new Map(node.properties.map((property) => [property.name, property]));
	const visibleProperties = schemaProperties.filter(
		(property) => property.kind !== "LUA_DERIVED" && evaluationsByName.get(property.name)?.applicable === true,
	);
	const fieldProperties = visibleProperties.filter((property) => !isSubConfigurationProperty(property));
	const subConfigurationProperties = visibleProperties.filter(
		(property) => property.kind === "INPUT" && isSubConfigurationProperty(property),
	);
	// Issues of properties without a field, such as sub-configurations, are shown above the fields.
	const fieldNames = new Set(fieldProperties.map((property) => property.name));
	const generalIssues = issues.filter((issue) => issue.propertyName == null || !fieldNames.has(issue.propertyName));

	return (
		<VerticalBox
			// The fields keep their height, with helper texts below them, and the form scrolls instead.
			sx={{ flex: 1, overflow: "auto", padding: 2, gap: 2, "& > *": { flexShrink: 0 } }}
		>
			{generalIssues.map((issue, index) => (
				<Alert key={index} severity={"error"}>
					{issue.message}
				</Alert>
			))}
			{fieldProperties.map((property) => (
				// A block wrapper, so that fields that grow along a row keep their own height in this column.
				<Box key={property.name}>
					<PropertyModelInputField
						property={property}
						evaluation={evaluationsByName.get(property.name)!}
						issueMessages={issues
							.filter((issue) => issue.propertyName === property.name)
							.map((issue) => issue.message)}
						disabled={readonly}
						onChange={(value) => onInputChanged(property.name, value)}
					/>
				</Box>
			))}
			{!readonly &&
				subConfigurationProperties.map((property) => {
					const unevaluatedIndexes = getUnevaluatedChildIndexes(inputValues, node.properties, property.name);
					if (unevaluatedIndexes.length === 0) return null;
					return (
						<AsyncButton
							key={property.name}
							icon={faTrash}
							variant={"outlined"}
							color={"error"}
							label={`${i18n.t("remove_unevaluated_sub_configurations")}: ${property.title || property.name}`}
							onClick={() => removeChildren(property.name, unevaluatedIndexes)}
							sx={{ alignSelf: "flex-start" }}
						/>
					);
				})}
		</VerticalBox>
	);
}
